import * as React from 'react';
import b_ from 'b_';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { formatNumber } from './format';

import './index.scss';

export { formatNumber } from './format';

export interface Props extends Omit<React.HTMLAttributes<HTMLSpanElement>, 'placeholder'> {
  /**
   * real number will be = (children) * 10^(exp)
   * if exp is undefined it will be just (children)
   * */
  exp?: number | null;
  isSvg?: boolean;
  className?: string;
  /**
   * maximum and minimum fraction digits
   */
  precision?: number;
  children?: number | null;
  /**
   * sign={true} - show '+' if number is positive
   * sign={false} - hide sign (even '-')
   * sign={undefined} - (@default) show only '-'
   */
  sign?: boolean;
  unit?: React.ReactNode;
  placeholder?: React.ReactNode;
  /** do not show .00 */
  hideZeroFraction?: boolean;
  minimumFractionDigits?: number;
  maximumFractionDigits?: number;
  /** sqweeze to mln, bln, trln if needed */
  compactNotation?: boolean;
  displayFractionOnLongNumbers?: boolean;
}

const RegularSpan = ({ children, ...props }: React.HTMLAttributes<any>) => (
  <span {...props}>{children}</span>
);
const SvgSpan = ({ children, ...props }: React.HTMLAttributes<any>) => (
  <tspan {...props}>{children}</tspan>
);

const getSign = (value: number, integer: string, fraction: string, showSign?: boolean) => {
  const fractionValue = fraction || 0;

  if (showSign === false) {
    return;
  }

  if (+integer === 0 && +fractionValue === 0) {
    return;
  }

  return (value < 0 || (value > 0 && showSign)) && (value < 0 ? '−' : '+');
};

const b = b_.with('number-view');

export const NumberView: React.FC<Props> = ({
  precision,
  placeholder,
  unit,
  isSvg,
  hideZeroFraction,
  className,
  exp,
  compactNotation,
  children: value,
  sign: showSign,
  minimumFractionDigits,
  maximumFractionDigits,
  displayFractionOnLongNumbers = false,
  ...props
}) => {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  if (typeof value === 'undefined' || value === null) {
    return <>{placeholder || null}</>;
  }

  let { integer, name, delim, fraction } = formatNumber(value, {
    language,
    maximumFractionDigits: maximumFractionDigits || precision || 0,
    minimumFractionDigits: minimumFractionDigits || precision || 0,
    exp,
    compactNotation,
  });

  if (
    (hideZeroFraction && parseInt(fraction) === 0) ||
    (integer.length > 6 && !displayFractionOnLongNumbers)
  ) {
    fraction = '';
  }

  const sign = getSign(value, integer, fraction, showSign);

  const Span = isSvg ? SvgSpan : RegularSpan;

  return (
    <Span className={cn(b(), className)} {...props}>
      {sign && <Span>{sign}</Span>}
      <Span className={b('integer')}>{integer.replace(' ', ' ') /* replace to thin space */}</Span>
      <Span className={b('fraction')}>
        {fraction && (
          <Span>
            {delim}
            {fraction}
          </Span>
        )}
      </Span>
      {name && <Span className={b('name')}> {t(name)}</Span>}
      <Span className={b('unit')}>{unit}</Span>
    </Span>
  );
};

export default NumberView;
